import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ColumnDef } from "@tanstack/react-table";
import { Pause, Play, Plus, RadioTower, Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  type BroadcastSummary,
  useBroadcastLifecycle,
  useBroadcasts,
  useDeleteBroadcast,
} from "@/lib/broadcasts";
import { formatNumber, formatRelative } from "@/lib/format";
import { BroadcastDrawer } from "@/components/broadcast/BroadcastDrawer";
import { CreateBroadcastDialog } from "@/components/broadcast/CreateBroadcastDialog";
import { useIntervalLabel } from "@/components/broadcast/useIntervalLabel";
import { HelpSection } from "@/components/HelpSection";
import { pushToast } from "@/components/Toaster";
import { StatusBadge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { ActionMenu } from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { DataTable } from "@/components/ui/table";

/**
 * Broadcasts: a prepared message posted into group chats again and again, from
 * one account or several. A created broadcast is running — the main action on
 * a row is "Pause", as with campaigns.
 */
export function BroadcastsPage() {
  const { t } = useTranslation();
  const interval = useIntervalLabel();
  const broadcasts = useBroadcasts();
  const pause = useBroadcastLifecycle("pause");
  const resume = useBroadcastLifecycle("resume");
  const del = useDeleteBroadcast();

  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const data = useMemo(() => broadcasts.data ?? [], [broadcasts.data]);

  const openDetail = (id: string) => {
    setDetailId(id);
    setDrawerOpen(true);
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await del.mutateAsync(deleteTarget.id);
      setDrawerOpen(false);
      pushToast({ kind: "success", text: t("broadcasts.deleted") });
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("broadcasts.action_error"),
      });
    }
  };

  const columns = useMemo<ColumnDef<BroadcastSummary, unknown>[]>(
    () => [
      {
        id: "name",
        header: t("broadcasts.col.name"),
        accessorFn: (b) => b.name,
        cell: ({ row }) => {
          const b = row.original;
          return (
            <div className="min-w-0">
              <span className="block truncate font-semibold text-fg-primary">{b.name}</span>
              <span className="block truncate text-xs text-fg-muted">
                {t(`broadcasts.mode.${b.mode}`)} ·{" "}
                {t("broadcasts.col.every", { interval: interval(b.interval_minutes) })}
              </span>
            </div>
          );
        },
      },
      {
        id: "chats",
        header: t("broadcasts.col.chats"),
        accessorFn: (b) => b.chat_count,
        cell: ({ row }) => {
          const b = row.original;
          return (
            <div className="text-xs text-fg-muted">
              <div className="font-mono text-sm tabular-nums text-fg-primary">
                {b.chats_ready}/{b.chat_count}
              </div>
              <div>{t("broadcasts.col.chats_ready")}</div>
            </div>
          );
        },
      },
      {
        id: "accounts",
        header: t("broadcasts.col.accounts"),
        accessorFn: (b) => b.account_count,
        cell: ({ row }) => {
          const b = row.original;
          return (
            <div className="text-xs text-fg-muted">
              <div className="font-mono text-sm tabular-nums text-fg-primary">
                {b.accounts_ready}/{b.account_count}
              </div>
              <div>{t("broadcasts.col.accounts_ready")}</div>
            </div>
          );
        },
      },
      {
        id: "posts",
        header: t("broadcasts.col.posts"),
        // Least urgent on a narrow screen; the card has the same numbers.
        meta: { className: "hidden min-[900px]:table-cell" },
        accessorFn: (b) => b.posts_24h,
        cell: ({ row }) => {
          const b = row.original;
          return (
            <div className="text-xs text-fg-muted">
              <div className="font-mono text-sm tabular-nums text-fg-primary">
                {formatNumber(b.posts_24h)}
              </div>
              <div>
                {t("broadcasts.col.posts_total", { count: b.posts_sent })}
                {b.posts_removed > 0 && (
                  <span className="text-warning">
                    {" · "}
                    {t("broadcasts.col.posts_removed", { count: b.posts_removed })}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: "status",
        header: t("broadcasts.col.status"),
        accessorFn: (b) => b.status,
        cell: ({ row }) => {
          const b = row.original;
          return (
            <div className="space-y-1">
              <StatusBadge
                domain="broadcast"
                value={b.waiting_reason ? "waiting" : b.status}
                label={t(`broadcasts.status.${b.waiting_reason ? "waiting" : b.status}`)}
                dot
              />
              <div className="max-w-[16rem] text-xs text-fg-muted">
                {b.waiting_reason
                  ? t(`broadcasts.waiting.${b.waiting_reason}`)
                  : b.last_post_at
                    ? t("broadcasts.col.last_post", { when: formatRelative(b.last_post_at) })
                    : b.status === "active"
                      ? t("broadcasts.col.no_posts_yet")
                      : null}
              </div>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">{t("broadcasts.col.actions")}</span>,
        enableSorting: false,
        cell: ({ row }) => {
          const b = row.original;
          return (
            <div
              className="flex items-center justify-end gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {b.status === "active" ? (
                <Button
                  size="sm"
                  variant="outline"
                  loading={pause.isPending && pause.variables?.id === b.id}
                  onClick={() => pause.mutate({ id: b.id })}
                  iconLeft={<Pause size={15} strokeWidth={2} aria-hidden="true" />}
                >
                  {t("broadcasts.pause")}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  loading={resume.isPending && resume.variables?.id === b.id}
                  onClick={() => resume.mutate({ id: b.id })}
                  iconLeft={<Play size={15} strokeWidth={2} aria-hidden="true" />}
                >
                  {t("broadcasts.resume")}
                </Button>
              )}
              <ActionMenu
                label={t("broadcasts.col.actions")}
                items={[
                  {
                    label: t("broadcasts.delete"),
                    icon: <Trash2 size={15} strokeWidth={2} aria-hidden="true" />,
                    onSelect: () => setDeleteTarget({ id: b.id, name: b.name }),
                    destructive: true,
                  },
                ]}
              />
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, pause.isPending, pause.variables, resume.isPending, resume.variables],
  );

  const subtitle = useMemo(() => {
    if (data.length === 0) return t("broadcasts.subtitle");
    return t("broadcasts.subtitle_counts", {
      total: data.length,
      active: data.filter((b) => b.status === "active").length,
      posts: formatNumber(data.reduce((n, b) => n + b.posts_24h, 0)),
    });
  }, [data, t]);

  const createButton = (
    <Button
      onClick={() => setCreating(true)}
      iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
    >
      {t("broadcasts.create.open")}
    </Button>
  );

  return (
    <section className="mx-auto max-w-7xl space-y-6 px-6 py-8">
      <PageHeader title={t("broadcasts.title")} subtitle={subtitle} actions={createButton} />

      {broadcasts.isLoading && <p className="text-fg-muted">{t("broadcasts.loading")}</p>}
      {broadcasts.isError && <p className="text-danger">{t("broadcasts.load_error")}</p>}

      {broadcasts.data && (
        <DataTable
          columns={columns}
          data={data}
          getRowId={(b) => b.id}
          onRowClick={(b) => openDetail(b.id)}
          empty={
            <EmptyState
              icon={RadioTower}
              title={t("broadcasts.empty_title")}
              description={t("broadcasts.empty_hint")}
              action={createButton}
            />
          }
        />
      )}

      <HelpSection
        i18nKey="broadcasts.help"
        paragraphs={4}
        list={{
          titleKey: "broadcasts.help.list_title",
          itemsKey: "broadcasts.help.list_items",
          itemCount: 5,
        }}
      />

      <CreateBroadcastDialog open={creating} onOpenChange={setCreating} onCreated={openDetail} />

      <BroadcastDrawer
        id={detailId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onDelete={(b) => setDeleteTarget({ id: b.id, name: b.name })}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        tone="danger"
        title={t("broadcasts.delete_confirm", { name: deleteTarget?.name ?? "" })}
        description={t("broadcasts.delete_confirm_hint")}
        confirmText={t("broadcasts.delete")}
        cancelText={t("common.cancel")}
        onConfirm={onDelete}
      />
    </section>
  );
}
